import {
  QueryClient,
  QueryCache,
  dehydrate,
  hydrate,
} from '@tanstack/react-query';
import { readStorageJSON, removeStorageItem, writeStorageJSON } from '../utils/storage';
import { clearBootstrapSnapshots } from '../hooks/authBootstrap/authBootstrapStorage';
import { ownsQueryScope, clearOfflineSnapshots, isAccessDenied, removeOfflineSnapshot, offlineNewsPreview } from './queryOwnership';
import { revokeQueryProjection } from './queryProjectionRevision';

const QUERY_CLIENT_CACHE_KEY = 'ls:react-query:cache';
const QUERY_CLIENT_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
// Persisting dehydrates and JSON-stringifies the whole cache synchronously on
// the main thread, and first load fires many cache events in quick succession.
// Keep the debounce long; the visibilitychange/beforeunload flushes below are
// what actually guarantee the cache is saved before the user leaves.
const PERSIST_DEBOUNCE_MS = 5_000;
const resetPersistedClients = new Set();
const CACHE_VERSION = 2;
// Current web data has no unconditional public persistence exemption. Only
// these deliberately approved, active-owner offline projections reach disk.
export const canPersistQuery = query => {
  const key = query.queryKey;
  if (query.meta?.persist === false || !Array.isArray(key) || key.length !== 3) return false;
  const [family, kind, scope] = key;
  if (!scope || typeof scope !== 'object' || !scope.actorId || !scope.sessionId
    || typeof scope.actorId !== 'string' || typeof scope.sessionId !== 'string'
    || typeof scope.communityId !== 'string' || !ownsQueryScope(scope)) return false;
  const fields = ['actorId', 'communityId', 'sessionId', ...(['activities', 'events'].includes(family) ? ['scope'] : [])];
  if (Object.keys(scope).some(field => !fields.includes(field))) return false;
  if (['activities', 'events'].includes(family)) return kind === 'list' && scope.scope === 'context';
  if (family === 'community') return ['meta', 'stats', 'lsa'].includes(kind);
  if (family === 'news') return ['preview', 'full-feed', 'recent-comments', 'has-fetched-full'].includes(kind);
  return family === 'notifications' && kind === 'user';
};
const offlineQuery = query => query.queryKey[0] === 'news' && ['preview', 'full-feed'].includes(query.queryKey[1])
  ? { ...query, state: { ...query.state, data: offlineNewsPreview(query.state.data) } } : query;


export const clearAppQueryClientPersistence = () => {
  // Removing storage alone lets a pending flush save the old account again.
  // Clear queries (including in-flight fetches) and discard scheduled writes.
  resetPersistedClients.forEach(reset => reset());
  clearBootstrapSnapshots();
  clearOfflineSnapshots();
  if (typeof window === 'undefined') return;
  removeStorageItem(QUERY_CLIENT_CACHE_KEY, { area: 'local' });
};

const isPersistedStateFresh = (persisted, now = Date.now()) => {
  if (!persisted || typeof persisted !== 'object') return false;
  const { savedAt, state } = persisted;
  if (!savedAt || !state || persisted.version !== CACHE_VERSION) return false;
  if (typeof savedAt !== 'number') return false;

  return now >= savedAt && now - savedAt <= QUERY_CLIENT_CACHE_TTL_MS;
};

const readPersistedQueryClientState = () => {
  const persisted = readStorageJSON(QUERY_CLIENT_CACHE_KEY, { area: 'local', fallback: null });
  if (!isPersistedStateFresh(persisted)) {
    if (persisted) {
      removeStorageItem(QUERY_CLIENT_CACHE_KEY, { area: 'local' });
    }
    return null;
  }
  // Never assign a legacy/foreign record to whichever actor signs in next.
  const state = {
    ...persisted.state,
    mutations: [],
    queries: Array.isArray(persisted.state.queries)
      ? persisted.state.queries.filter(canPersistQuery).map(offlineQuery)
      : [],
  };
  if (JSON.stringify(state) !== JSON.stringify(persisted.state)) {
    writeStorageJSON(QUERY_CLIENT_CACHE_KEY, { ...persisted, state }, { area: 'local' });
  }
  return state;
};

const persistQueryClientState = (queryClient) => {
  const state = dehydrate(queryClient, {
    shouldDehydrateMutation: () => false,
    shouldDehydrateQuery: query => query.state.status === 'success' && canPersistQuery(query),
  });
  state.queries = state.queries.map(offlineQuery);
  writeStorageJSON(
    QUERY_CLIENT_CACHE_KEY,
    {
      version: CACHE_VERSION,
      savedAt: Date.now(),
      state,
    },
    { area: 'local' },
  );
};

export const setupAppQueryClientPersistence = (queryClient) => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const persistedState = readPersistedQueryClientState();
  if (persistedState) {
    try {
      hydrate(queryClient, persistedState);
    } catch {
      removeStorageItem(QUERY_CLIENT_CACHE_KEY, { area: 'local' });
    }
  }

  let persistTimeoutId = null;
  let hasPendingChanges = false;
  let resetting = false;
  const reset = () => {
    resetting = true;
    try {
      queryClient.clear();
      if (persistTimeoutId !== null) clearTimeout(persistTimeoutId);
      persistTimeoutId = null;
      hasPendingChanges = false;
    } finally {
      resetting = false;
    }
  };
  resetPersistedClients.add(reset);
  const flushPersist = () => {
    if (persistTimeoutId !== null) clearTimeout(persistTimeoutId);
    persistTimeoutId = null;
    if (!hasPendingChanges) return;
    try {
      persistQueryClientState(queryClient);
      hasPendingChanges = false;
    } catch {
      // Persistence is optional, including during browser lifecycle events.
    }
  };

  const schedulePersist = () => {
    if (resetting) return;
    hasPendingChanges = true;
    if (persistTimeoutId !== null) clearTimeout(persistTimeoutId);
    persistTimeoutId = window.setTimeout(flushPersist, PERSIST_DEBOUNCE_MS);
  };

  const cleanupCacheSubscription = queryClient.getQueryCache().subscribe((event) => {
    // Observer notifications and fetch progress don't change reusable data.
    // Persist data, errors, invalidation, resets, and additions/removals only.
    if (
      event.type === 'added'
      || event.type === 'removed'
      || (event.type === 'updated'
        && ['success', 'error', 'invalidate', 'setState'].includes(event.action.type))
    ) {
      schedulePersist();
    }
  });

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      flushPersist();
    }
  };

  const handleBeforeUnload = flushPersist;

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('beforeunload', handleBeforeUnload);

  return () => {
    resetPersistedClients.delete(reset);
    if (persistTimeoutId !== null) clearTimeout(persistTimeoutId);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('beforeunload', handleBeforeUnload);
    cleanupCacheSubscription();
  };
};

export const createAppQueryClient = () => {
  const client = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (!isAccessDenied(error) && error?.code !== 'INVALID_API_RESPONSE') return;
        revokeQueryProjection(client, query.queryKey);
        // Undefined causes initialData to be adopted again; null preserves the
        // error while erasing the projection across all active observers.
        query.setState({ data: null, dataUpdatedAt: 0 });
        const [family, kind, scope] = query.queryKey;
        const snapshots = {
          community: { meta: 'communityMeta', stats: 'communityStats', lsa: 'lsa' },
          news: { preview: 'previewFeed', 'recent-comments': 'recentComments' },
          notifications: { user: 'notifications' },
        };
        if (snapshots[family]?.[kind]) removeOfflineSnapshot(snapshots[family][kind], scope);
      },
    }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (count, error) => !isAccessDenied(error) && count < 1,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 0,
    },
  },
  });
  return client;
};

const appQueryClient = createAppQueryClient();

export default appQueryClient;
